import {HTML} from "imperative-html/dist/esm/elements-strict.js";
import {Prompt} from "./Prompt.js";
import {SongDocument} from "./SongDocument.js";
import {ChangeSong} from "./changes.js";
import {EditorConfig} from "./EditorConfig.js";
import {Instrument} from "../synth/synth.js";
import {Config} from "../synth/SynthConfig.js";
import {songToText, songFromText, songReadout} from "./SongText.js";
const {div,h2,p,textarea,select,option,button,input,a} = HTML;
export class SongTextPrompt implements Prompt {
    private readonly _view = select({"aria-label":"Text view"}, option({value:"score"},"Score (editable)"), option({value:"readout"},"Readout (for review)"), option({value:"agent"},"Agent handoff"));
    private readonly _text = textarea({"aria-label":"Song text", spellcheck:"false", rows:16});
    private readonly _file = input({type:"file",accept:".txt,.json,text/plain,application/json","aria-label":"Load a Bop text score"});
    private readonly _status = p({role:"status","aria-live":"polite",class:"song-text-status"});
    private readonly _copy = button({type:"button"},"Copy text");
    private readonly _download = button({type:"button"},"Download text");
    private readonly _apply = button({type:"button"},"Apply score");
    private readonly _closeButton = button({type:"button",class:"cancelButton","aria-label":"Close dialog"});
    public readonly container = div({class:"prompt noSelection song-text-prompt"},
        h2("Song text / LLM"),
        p("Share the score or an agent handoff with your assistant. An agent can inspect and revise the music through the website."),
        p("For MIDI, use File → Import Song or Export Song (.mid), then open this view to read the imported music."),
        p(a({href: location.protocol === "file:" ? "https://sahildev.com/bop/agent-guide.txt" : "/bop/agent-guide.txt",target:"_blank",rel:"noopener"},"Format guide & LLM instructions ↗")),
        this._view,this._text,
        div({class:"song-text-actions"},this._copy,this._download,this._apply),
        this._file,this._status,p("Applying replaces the song in one undoable step. Changing a repeated pattern changes every occurrence."),this._closeButton,
    );
    private _draft: string;
    private _timer: ReturnType<typeof setTimeout>;
    private _closed = false;
    private _label = (instrument: Instrument): string => EditorConfig.valueToPreset(instrument.preset)?.name || Config.instrumentTypeNames[instrument.type];
    constructor(private readonly _doc: SongDocument) {
        this._draft = songToText(_doc.song,this._label); this._text.value = this._draft;
        this._text.addEventListener("input",this._edit);
        this._view.addEventListener("change",this._switch);
        this._copy.addEventListener("click",this._copyText);
        this._download.addEventListener("click",this._save);
        this._apply.addEventListener("click",this._use);
        this._closeButton.addEventListener("click",this._close);
        this._file.addEventListener("change",this._load);
        this._timer = setTimeout(()=>{ this._text.focus(); this._text.setSelectionRange(0,0); this._text.scrollTop = 0; });
    }
    private _edit = (): void => { this._draft = this._text.value; this._status.textContent = ""; };
    private _switch = (): void => {
        try {
            const readout = this._view.value !== "score";
            const song = readout ? songFromText(this._draft) : null;
            this._text.value = this._view.value === "agent" ?
                "Help me revise this Bop song. Read https://sahildev.com/bop/agent-guide.txt for the website-only API v3 workflow. Import the song URL, inspect the relevant tracks and bars, then edit using its revision and track IDs. Keep the existing instrument palette unless I request a change. Return a validated editor or player link and explain the changes.\n\nSong: " + new URL("#"+song!.toBase64String(),"https://sahildev.com"+(/^\/bop\/releases\/\d+\.\d+\.\d+\/$/.test(location.pathname)?location.pathname:"/bop/")).href + "\n\nMy requested changes: " :
                readout ? songReadout(song!,this._label) : this._draft;
            this._text.setSelectionRange(0,0); this._text.scrollTop = 0;
            this._text.readOnly = readout; this._apply.disabled = readout; this._status.textContent = "";
        } catch(error) { this._view.value = "score"; this._status.textContent = (error as Error).message; }
    };
    private _copyText = async (): Promise<void> => {
        try { await navigator.clipboard.writeText(this._text.value); this._status.textContent = "Text copied."; }
        catch { this._text.focus(); this._text.select(); this._status.textContent = "Text selected. Use your keyboard’s copy command."; }
    };
    private _save = (): void => {
        const url = URL.createObjectURL(new Blob([this._text.value],{type:"text/plain;charset=utf-8"}));
        const a = document.createElement("a"); a.href = url; a.download = this._view.value === "score" ? "song.bop.txt" : this._view.value === "agent" ? "song.agent.txt" : "song.readout.txt"; a.click();
        setTimeout(()=>URL.revokeObjectURL(url),1000);
    };
    private _load = async (): Promise<void> => {
        const file = this._file.files?.[0]; if (!file) return;
        try {
            if (file.size > 4000000) throw new Error("Score file is too large (maximum 4 MB).");
            const text = await file.text(); if (this._closed) return;
            songFromText(text); // Validate before replacing the editable draft.
            this._draft = text; this._view.value = "score"; this._switch();
            this._status.textContent = "Score loaded for review. Apply score when ready.";
        } catch(error) { this._status.textContent = (error as Error).message; }
        this._file.value = "";
    };
    private _use = (): void => {
        try {
            const song = songFromText(this._draft);
            this._doc.prompt = null; this._doc.goBackToStart(); this._doc.selection.resetBoxSelection();
            this._doc.record(new ChangeSong(this._doc,song.toBase64String()),true,true);
        } catch(error) { this._status.textContent = (error as Error).message; }
    };
    private _close = (): void => { this._doc.undo(); };
    public cleanUp(): void {
        this._closed = true; clearTimeout(this._timer);
        this._text.removeEventListener("input",this._edit); this._view.removeEventListener("change",this._switch);
        this._copy.removeEventListener("click",this._copyText); this._download.removeEventListener("click",this._save);
        this._apply.removeEventListener("click",this._use); this._closeButton.removeEventListener("click",this._close);
        this._file.removeEventListener("change",this._load);
    }
}
